/**
 * useColorTokens - fetch Airtable Color Tokens once and inject them as CSS
 * custom properties on :root, so any component can read:
 *
 *   color: var(--btn-primary-text-default);
 *   background-color: var(--btn-primary-bg-default);
 *   &:hover { background-color: var(--btn-primary-bg-hover); }
 *
 * Token naming convention (derived from Airtable `Token Path`):
 *   button.primary  ->  --btn-primary-bg-default / --btn-primary-bg-hover
 *                        --btn-primary-bg-pressed / --btn-primary-bg-disabled
 *                        --btn-primary-bg-inverse
 *                        --btn-primary-text-default / --btn-primary-text-hover
 *   tag.option      ->  --tag-option-bg-default / -hover / -pressed / -disabled / -inverse
 *                        --tag-option-text-default / -hover
 *
 * Drop <ColorTokensProvider /> once near the app root and every descendant
 * (existing or future) gets the tokens for free. Existing components keep
 * working unchanged because the CSS vars are additive.
 *
 * The opt-in <TokenButton> / <TokenInput> wrappers in components/cms/Token.jsx
 * read from these vars by default.
 */
import { useEffect } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _injected = false;
let _tokens = null;

function pathToCssPrefix(p) {
  // button.primary -> btn-primary, tag.option -> tag-option, link.default -> link-default
  return (p || "")
    .replace(/^button\./, "btn.")
    .replace(/\./g, "-");
}

function injectTokens(tokens) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  tokens.forEach((t) => {
    const prefix = pathToCssPrefix(t.token_path);
    if (!prefix) return;
    if (t.default_hex) root.style.setProperty(`--${prefix}-bg-default`, t.default_hex);
    if (t.hover_hex) root.style.setProperty(`--${prefix}-bg-hover`, t.hover_hex);
    if (t.pressed_hex) root.style.setProperty(`--${prefix}-bg-pressed`, t.pressed_hex);
    if (t.disabled_hex) root.style.setProperty(`--${prefix}-bg-disabled`, t.disabled_hex);
    if (t.inverse_hex) root.style.setProperty(`--${prefix}-bg-inverse`, t.inverse_hex);
    if (t.text_on_default) root.style.setProperty(`--${prefix}-text-default`, t.text_on_default);
    if (t.text_on_hover) root.style.setProperty(`--${prefix}-text-hover`, t.text_on_hover);
  });
}

/**
 * Mount-and-inject hook. Pulls /api/ui/color-tokens once per page load and
 * sets every token onto :root.
 */
export function useColorTokens() {
  useEffect(() => {
    if (_injected) return;
    _injected = true;
    fetch(`${API}/ui/color-tokens`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.tokens) return;
        _tokens = d.tokens;
        injectTokens(d.tokens);
      })
      .catch(() => {
        // Fallback: leave the existing tailwind palette untouched. The opt-in
        // <TokenButton> uses sensible defaults (#000 / #1f2937) when vars
        // are unset, so nothing visually breaks.
        _injected = false;
      });
  }, []);
}

/**
 * <ColorTokensProvider> - drop once at the app root. Fetches & injects tokens.
 * Renders nothing visible.
 */
export function ColorTokensProvider({ children }) {
  useColorTokens();
  return children || null;
}

/** Read a previously-fetched token (or null if not loaded yet). */
export function getColorToken(tokenPath) {
  if (!_tokens) return null;
  return _tokens.find((t) => t.token_path === tokenPath) || null;
}
